import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { GraduationCap, Plus, Pencil, Trash2, Users, ChevronRight, LayoutGrid, List, BookOpen, Upload, MessageCircleQuestion, Award, MessagesSquare, Radio } from "lucide-react";
import TraineeDetail from "@/components/TraineeDetail";
import TheoryCourses from "@/components/theory/TheoryCourses";
import QaReview from "@/components/theory/QaReview";
import StudentQuestions from "@/components/theory/StudentQuestions";
import VfrgCourses from "@/components/theory/VfrgCourses";
import AtcRadio from "@/components/theory/AtcRadio";
import CertificatesPage from "@/pages/CertificatesPage";
import ProgramBuilder from "@/components/ProgramBuilder";
import ProgramOverview from "@/components/ProgramOverview";
import { useAuth } from "@/context/AuthContext";

const DISC = {
  drone: { label: "Drone", cls: "bg-lime-100 text-lime-700 border-lime-200" },
  helicopter: { label: "Helicopter", cls: "bg-sky-100 text-sky-700 border-sky-200" },
  aeroplane: { label: "Fixed Wing", cls: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  other: { label: "Other", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
const SYLLABUS_TABS = [
  { key: "drone", label: "Drone Syllabus" },
  { key: "helicopter", label: "Heli Syllabus" },
  { key: "aeroplane", label: "Fixed Wing Syllabus" },
];
const emptyTrainee = { first_name: "", last_name: "", email: "", phone: "", reference_no: "", medical_class: "", medical_expiry: "", notes: "", program_ids: [], status: "active", user_id: null };

export default function TrainingPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("trainees");
  const [trainees, setTrainees] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [selected, setSelected] = useState(null); // trainee id for detail
  const [viewProgram, setViewProgram] = useState(null); // program object for overview
  const [tForm, setTForm] = useState(emptyTrainee);
  const [tOpen, setTOpen] = useState(false);
  const [savingT, setSavingT] = useState(false);
  const [builder, setBuilder] = useState(null); // {program} or {new:true}
  const [delT, setDelT] = useState(null);
  const [delP, setDelP] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem("training_view") || "grid");
  const [studentUsers, setStudentUsers] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importName, setImportName] = useState("");
  const [importing, setImporting] = useState(false);

  const doImport = async () => {
    if (!importFile || !importName.trim()) { toast.error("Choose a file and enter a program name"); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      fd.append("name", importName.trim());
      fd.append("discipline", tab === "trainees" || tab === "theory" ? "helicopter" : tab);
      const r = await api.post("/training/import-syllabus", fd);
      toast.success(`Imported ${r.data.lessons_imported} lessons (${r.data.criteria_imported} criteria)`);
      setImportOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); } finally { setImporting(false); }
  };
  const setViewMode = (v) => { setView(v); localStorage.setItem("training_view", v); };

  const load = useCallback(async () => {
    const [t, p] = await Promise.all([api.get("/training/trainees"), api.get("/training/programs")]);
    setTrainees(t.data); setPrograms(p.data);
    try { const u = await api.get("/users"); setStudentUsers((u.data || []).filter((x) => x.role === "student" || x.role === "trainee" || (x.roles || []).includes("student"))); } catch { /* non-admins can't list users */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const progName = (id) => programs.find((p) => p.id === id)?.name || "No program";
  const tabPrograms = useMemo(() => programs.filter((p) => p.discipline === tab), [programs, tab]);
  const traineePrograms = (t) => (t.program_ids?.length ? t.program_ids : (t.program_id ? [t.program_id] : []));
  const progNames = (ids) => (ids && ids.length ? ids.map((id) => programs.find((p) => p.id === id)?.name).filter(Boolean).join(", ") : "No program");
  const setT = (k, v) => setTForm((f) => ({ ...f, [k]: v }));
  const toggleProgram = (id) => setTForm((f) => {
    const ids = f.program_ids || [];
    return { ...f, program_ids: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] };
  });
  const openNewT = () => { setTForm(emptyTrainee); setTOpen(true); };
  const openEditT = (t) => { setTForm({ ...emptyTrainee, ...t, program_ids: traineePrograms(t) }); setTOpen(true); };

  const saveT = async () => {
    if (!tForm.first_name.trim()) { toast.error("First name is required"); return; }
    setSavingT(true);
    const payload = { ...tForm, program_ids: tForm.program_ids || [] };
    delete payload.id; delete payload.created_at; delete payload.program_id;
    try {
      if (tForm.id) await api.put(`/training/trainees/${tForm.id}`, payload);
      else await api.post("/training/trainees", payload);
      toast.success("Trainee saved"); setTOpen(false); load();
    } catch { toast.error("Save failed"); } finally { setSavingT(false); }
  };
  const removeT = async (t) => { try { await api.delete(`/training/trainees/${t.id}`); toast.success("Trainee removed"); load(); } catch { toast.error("Delete failed"); } finally { setDelT(null); } };
  const removeP = async (p) => { try { await api.delete(`/training/programs/${p.id}`); toast.success("Program deleted"); load(); } catch { toast.error("Delete failed"); } finally { setDelP(null); } };

  const builderEl = builder && (
    <ProgramBuilder
      key={builder.program?.id || "new"}
      program={builder.program || null}
      open={!!builder}
      onOpenChange={(v) => !v && setBuilder(null)}
      onSaved={async () => {
        setBuilder(null);
        const [t, p] = await Promise.all([api.get("/training/trainees"), api.get("/training/programs")]);
        setTrainees(t.data); setPrograms(p.data);
        if (viewProgram) setViewProgram(p.data.find((x) => x.id === viewProgram.id) || null);
      }}
    />
  );

  if (selected) return <TraineeDetail traineeId={selected} onBack={() => { setSelected(null); load(); }} />;
  if (viewProgram) return (
    <>
      <ProgramOverview
        program={viewProgram}
        onBack={() => setViewProgram(null)}
        onEdit={() => { setBuilder({ program: viewProgram }); }}
        onAssign={() => { setTForm({ ...emptyTrainee, program_ids: [viewProgram.id] }); setViewProgram(null); setTab("trainees"); setTOpen(true); }}
      />
      {builderEl}
    </>
  );

  return (
    <div className="space-y-6 fade-up" data-testid="training-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-lime-600 mb-1">Pilot Training</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading flex items-center gap-2">
            <GraduationCap className="h-8 w-8 text-lime-500" /> Training
          </h1>
          <p className="text-slate-500 mt-1">Competency-based syllabi and trainee progress — for drones, helicopters or planes.</p>
        </div>
        {tab === "trainees"
          ? <Button data-testid="new-trainee-btn" onClick={openNewT} className="bg-slate-900 hover:bg-slate-800 text-white"><Plus className="h-4 w-4 mr-1.5" /> New Trainee</Button>
          : (tab === "theory" || tab === "atc" || tab === "vfrg" || tab === "certificates" || tab === "qa" || tab === "student-questions")
          ? null
          : <div className="flex items-center gap-2">
              <Button data-testid="import-syllabus-btn" onClick={() => { setImportName(""); setImportFile(null); setImportOpen(true); }} variant="outline" className="border-slate-200"><Upload className="h-4 w-4 mr-1.5" /> Import from Excel</Button>
              <Button data-testid="new-program-btn" onClick={() => setBuilder({ program: { discipline: tab } })} className="bg-slate-900 hover:bg-slate-800 text-white"><Plus className="h-4 w-4 mr-1.5" /> New Program</Button>
            </div>}
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex flex-wrap rounded-lg border border-slate-200 bg-white p-0.5" data-testid="training-tabs">
          <button data-testid="tab-trainees" onClick={() => setTab("trainees")} className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-md transition-colors ${tab === "trainees" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}><Users className="h-4 w-4" /> Trainees</button>
          <button data-testid="tab-theory" onClick={() => setTab("theory")} className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-md transition-colors ${tab === "theory" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}><BookOpen className="h-4 w-4" /> Theory Courses</button>
          <button data-testid="tab-vfrg" onClick={() => setTab("vfrg")} className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-md transition-colors ${tab === "vfrg" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}><BookOpen className="h-4 w-4" /> VFRG</button>
          <button data-testid="tab-atc" onClick={() => setTab("atc")} className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-md transition-colors ${tab === "atc" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}><Radio className="h-4 w-4" /> ATC Radio</button>
          <button data-testid="tab-certificates" onClick={() => setTab("certificates")} className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-md transition-colors ${tab === "certificates" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}><Award className="h-4 w-4" /> Certificates</button>
          {SYLLABUS_TABS.map((s) => (
            <button key={s.key} data-testid={`tab-${s.key}`} onClick={() => setTab(s.key)} className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-md transition-colors ${tab === s.key ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}>{s.label}</button>
          ))}
          {user?.role === "admin" && (
            <button data-testid="tab-qa" onClick={() => setTab("qa")} className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-md transition-colors ${tab === "qa" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}><MessageCircleQuestion className="h-4 w-4" /> Q&amp;A Review</button>
          )}
          {user?.role === "admin" && (
            <button data-testid="tab-student-questions" onClick={() => setTab("student-questions")} className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-md transition-colors ${tab === "student-questions" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}><MessagesSquare className="h-4 w-4" /> Student Q&apos;s</button>
          )}
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-testid="training-view-toggle" style={{ display: ["qa", "student-questions", "atc"].includes(tab) ? "none" : undefined }}>
          <button onClick={() => setViewMode("grid")} data-testid="training-view-grid" aria-label="Tile view"
            className={`px-3 py-2 flex items-center gap-1.5 text-sm ${view === "grid" ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}><LayoutGrid className="h-4 w-4" /> Tiles</button>
          <button onClick={() => setViewMode("list")} data-testid="training-view-list" aria-label="List view"
            className={`px-3 py-2 flex items-center gap-1.5 text-sm border-l border-slate-200 ${view === "list" ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}><List className="h-4 w-4" /> List</button>
        </div>
      </div>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent data-testid="import-syllabus-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Import syllabus from Excel</DialogTitle>
            <DialogDescription>Upload a CASA-style workbook. Each numbered lesson sheet becomes a flight lesson with its MOS assessment grid (elements, performance criteria &amp; required standards).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Program name</Label>
              <Input value={importName} onChange={(e) => setImportName(e.target.value)} className="mt-1.5" placeholder="e.g. PPL(H) Syllabus" data-testid="import-name-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Excel file (.xlsx)</Label>
              <Input type="file" accept=".xlsx" onChange={(e) => setImportFile(e.target.files?.[0] || null)} className="mt-1.5" data-testid="import-file-input" />
              {importFile && <p className="text-xs text-slate-500 mt-1.5">{importFile.name}</p>}
            </div>
            <p className="text-xs text-slate-400">Discipline: <span className="capitalize font-medium text-slate-600">{tab === "trainees" || tab === "theory" ? "helicopter" : tab}</span> (from the current tab)</p>
            <Button onClick={doImport} disabled={importing} className="w-full bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold" data-testid="import-confirm-btn">
              <Upload className="h-4 w-4 mr-1.5" /> {importing ? "Importing…" : "Import syllabus"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {tab === "theory" ? (
        <TheoryCourses view={view} />
      ) : tab === "vfrg" ? (
        <VfrgCourses view={view} />
      ) : tab === "atc" ? (
        <AtcRadio />
      ) : tab === "certificates" ? (
        <CertificatesPage view={view} />
      ) : tab === "qa" ? (
        <QaReview />
      ) : tab === "student-questions" ? (
        <StudentQuestions />
      ) : tab === "trainees" ? (
        view === "list" ? (
          <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden" data-testid="trainees-list">
            {trainees.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">No trainees yet. Add one to start tracking training.</p>}
            {trainees.map((t) => (
              <div key={t.id} data-testid={`trainee-row-${t.id}`} className="flex items-center gap-4 px-4 py-3 hover:bg-slate-50">
                <button onClick={() => setSelected(t.id)} className="min-w-0 flex-1 text-left" data-testid={`open-trainee-${t.id}`}>
                  <p className="font-medium text-slate-900 truncate">{t.first_name} {t.last_name}</p>
                  <p className="text-xs text-slate-500 truncate">{progNames(traineePrograms(t))}</p>
                </button>
                {t.reference_no && <Badge variant="outline" className="border-slate-200 hidden sm:inline-flex">{t.reference_no}</Badge>}
                <Badge className={`border ${t.status === "active" ? "bg-lime-50 text-lime-700 border-lime-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>{t.status}</Badge>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-slate-900" data-testid={`edit-trainee-${t.id}`} onClick={() => openEditT(t)}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" data-testid={`delete-trainee-${t.id}`} onClick={() => setDelT(t)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="trainees-grid">
          {trainees.length === 0 && <p className="text-sm text-slate-400 col-span-full py-8 text-center">No trainees yet. Add one to start tracking training.</p>}
          {trainees.map((t) => (
            <div key={t.id} data-testid={`trainee-card-${t.id}`} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-all min-w-0">
              <div className="flex items-start justify-between gap-2">
                <button onClick={() => setSelected(t.id)} className="min-w-0 text-left flex-1" data-testid={`open-trainee-${t.id}`}>
                  <h3 className="font-heading font-semibold text-slate-900 truncate flex items-center gap-1.5">{t.first_name} {t.last_name} <ChevronRight className="h-4 w-4 text-slate-300" /></h3>
                  <p className="text-xs text-slate-500 truncate mt-0.5">{progNames(traineePrograms(t))}</p>
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-slate-900" data-testid={`edit-trainee-${t.id}`} onClick={() => openEditT(t)}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" data-testid={`delete-trainee-${t.id}`} onClick={() => setDelT(t)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap text-xs text-slate-500">
                {t.reference_no && <Badge variant="outline" className="border-slate-200">{t.reference_no}</Badge>}
                <Badge className={`border ${t.status === "active" ? "bg-lime-50 text-lime-700 border-lime-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>{t.status}</Badge>
              </div>
            </div>
          ))}
        </div>
        )
      ) : (
        view === "list" ? (
          <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden" data-testid="programs-list">
            {tabPrograms.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">No {DISC[tab]?.label} syllabus yet. Click "New Program" to create one.</p>}
            {tabPrograms.map((p) => {
              const d = DISC[p.discipline] || DISC.other;
              return (
                <div key={p.id} data-testid={`program-row-${p.id}`} className="flex items-center gap-4 px-4 py-3 hover:bg-slate-50">
                  <button onClick={() => setViewProgram(p)} className="min-w-0 flex-1 text-left" data-testid={`open-program-${p.id}`}>
                    <p className="font-medium text-slate-900 truncate">{p.name}</p>
                    <p className="text-xs text-slate-500 truncate">{p.phases?.length || 0} phases · {p.lessons?.length || 0} lessons · {p.theory_units?.length || 0} theory</p>
                  </button>
                  {p.regulatory_ref && <span className="text-xs text-slate-400 hidden sm:inline">{p.regulatory_ref}</span>}
                  <Badge className={`border ${d.cls}`}>{d.label}</Badge>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-slate-900" data-testid={`edit-program-${p.id}`} onClick={() => setBuilder({ program: p })}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" data-testid={`delete-program-${p.id}`} onClick={() => setDelP(p)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="programs-grid">
          {tabPrograms.length === 0 && (
            <p className="text-sm text-slate-400 col-span-full py-8 text-center">No {DISC[tab]?.label} syllabus yet. Click "New Program" to create one.</p>
          )}
          {tabPrograms.map((p) => {
            const d = DISC[p.discipline] || DISC.other;
            return (
              <div key={p.id} data-testid={`program-card-${p.id}`} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-all min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <button onClick={() => setViewProgram(p)} className="min-w-0 text-left flex-1" data-testid={`open-program-${p.id}`}>
                    <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-1.5">{p.name} <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" /></h3>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Badge className={`border ${d.cls}`}>{d.label}</Badge>
                      {p.regulatory_ref && <span className="text-xs text-slate-400">{p.regulatory_ref}</span>}
                    </div>
                  </button>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-slate-900" data-testid={`edit-program-${p.id}`} onClick={() => setBuilder({ program: p })}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" data-testid={`delete-program-${p.id}`} onClick={() => setDelP(p)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
                <button onClick={() => setViewProgram(p)} className="text-sm text-slate-500 mt-3 hover:text-slate-800">{p.phases?.length || 0} phases · {p.lessons?.length || 0} lessons · {p.theory_units?.length || 0} theory</button>
              </div>
            );
          })}
        </div>
        )
      )}

      {/* Trainee dialog */}
      <Dialog open={tOpen} onOpenChange={setTOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="trainee-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{tForm.id ? "Edit Trainee" : "New Trainee"}</DialogTitle><DialogDescription>Trainee profile & assigned program.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            {studentUsers.length > 0 && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Link to Student login <span className="normal-case font-normal text-slate-400">(optional — prefills details)</span></Label>
                <Select value={tForm.user_id || "none"} onValueChange={(v) => {
                  if (v === "none") { setT("user_id", null); return; }
                  const u = studentUsers.find((x) => x.user_id === v);
                  setTForm((f) => ({ ...f, user_id: v,
                    first_name: f.first_name || u?.first_name || "", last_name: f.last_name || u?.last_name || "",
                    email: f.email || u?.email || "", phone: f.phone || u?.mobile || "" }));
                }}>
                  <SelectTrigger className="mt-1.5" data-testid="trainee-user-link"><SelectValue placeholder="Not linked" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not linked to a login</SelectItem>
                    {studentUsers.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.email})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">First name</Label><Input value={tForm.first_name} onChange={(e) => setT("first_name", e.target.value)} className="mt-1.5" data-testid="trainee-first-name" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Last name</Label><Input value={tForm.last_name} onChange={(e) => setT("last_name", e.target.value)} className="mt-1.5" data-testid="trainee-last-name" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</Label><Input value={tForm.email} onChange={(e) => setT("email", e.target.value)} className="mt-1.5" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Phone</Label><Input value={tForm.phone} onChange={(e) => setT("phone", e.target.value)} className="mt-1.5" /></div>
            </div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Programs <span className="text-slate-400 normal-case font-normal">(assign one or more)</span></Label>
              <div className="mt-1.5 border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-48 overflow-y-auto" data-testid="trainee-programs-list">
                {programs.length === 0 && <p className="text-sm text-slate-400 px-3 py-3">No programs yet — create a syllabus first.</p>}
                {programs.map((p) => {
                  const checked = (tForm.program_ids || []).includes(p.id);
                  const d = DISC[p.discipline] || DISC.other;
                  return (
                    <label key={p.id} data-testid={`trainee-program-option-${p.id}`} className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer ${checked ? "bg-lime-50" : "hover:bg-slate-50"}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleProgram(p.id)} className="accent-lime-500 h-4 w-4" />
                      <span className="text-sm text-slate-800 flex-1 truncate">{p.name}</span>
                      <Badge className={`border text-[10px] ${d.cls}`}>{d.label}</Badge>
                    </label>
                  );
                })}
              </div>
              {(tForm.program_ids || []).length > 0 && <p className="text-xs text-slate-400 mt-1.5">{tForm.program_ids.length} program{tForm.program_ids.length > 1 ? "s" : ""} selected</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Reference / Licence no.</Label><Input value={tForm.reference_no} onChange={(e) => setT("reference_no", e.target.value)} className="mt-1.5" placeholder="ARN / licence" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Status</Label>
                <Select value={tForm.status} onValueChange={(v) => setT("status", v)}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{["active", "completed", "inactive"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Medical class</Label><Input value={tForm.medical_class} onChange={(e) => setT("medical_class", e.target.value)} className="mt-1.5" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Medical expiry</Label><Input type="date" value={tForm.medical_expiry || ""} onChange={(e) => setT("medical_expiry", e.target.value)} className="mt-1.5" /></div>
            </div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Notes</Label><Textarea value={tForm.notes} onChange={(e) => setT("notes", e.target.value)} className="mt-1.5" rows={2} /></div>
            <Button onClick={saveT} disabled={savingT} className="w-full bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold" data-testid="save-trainee-btn">{savingT ? "Saving…" : "Save Trainee"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {builderEl}

      <AlertDialog open={!!delT} onOpenChange={(v) => !v && setDelT(null)}>
        <AlertDialogContent data-testid="delete-trainee-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {delT?.first_name} {delT?.last_name}?</AlertDialogTitle>
            <AlertDialogDescription>This deletes the trainee and all their lesson progress records. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-trainee-btn" onClick={() => removeT(delT)} className="bg-red-600 hover:bg-red-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!delP} onOpenChange={(v) => !v && setDelP(null)}>
        <AlertDialogContent data-testid="delete-program-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{delP?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>This deletes the syllabus program. Trainees assigned to it will keep their profile but lose the linked lessons. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-program-btn" onClick={() => removeP(delP)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
